import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUpdateBrandNameMutation } from '@/hooks/useBrandingMutations';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

interface BrandNameFormProps {
  brandName: string | null;
}

export function BrandNameForm({ brandName }: BrandNameFormProps) {
  const { t } = useTranslation('modules');
  const updateMutation = useUpdateBrandNameMutation();
  const [value, setValue] = useState(brandName === null ? '' : brandName);

  function submit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    const trimmed = value.trim();
    updateMutation.mutate(trimmed.length === 0 ? null : trimmed, {
      onSuccess: function notifySaved() {
        showSuccessNotification(t('branding.saved'));
      },
      onError: function notifyError(saveError) {
        const backendMessage = resolveApiErrorMessage(saveError);
        showErrorNotification(backendMessage.length > 0 ? backendMessage : t('branding.saveError'));
      },
    });
  }

  return (
    <form onSubmit={submit} className="bg-zoowhite border border-zooborder rounded-2xl p-6 shadow-sm">
      <h2 className="text-sm font-bold text-zooindigo">{t('branding.brandNameTitle')}</h2>
      <p className="text-xs text-zoomuted mt-1 mb-4">{t('branding.brandNameHint')}</p>
      <div className="space-y-1.5 max-w-md">
        <Label htmlFor="brand-name">{t('branding.brandNameLabel')}</Label>
        <Input
          id="brand-name"
          value={value}
          placeholder={t('branding.brandNamePlaceholder')}
          onChange={function updateValue(changeEvent) {
            setValue(changeEvent.target.value);
          }}
        />
      </div>
      <div className="mt-4">
        <Button type="submit" isLoading={updateMutation.isPending}>
          {t('branding.save')}
        </Button>
      </div>
    </form>
  );
}
